import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Wand2, FileText, Link as LinkIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import ResultsDashboard from "@/components/content-optimizer/ResultsDashboard";
import Recommendations from "@/components/content-optimizer/Recommendations";

export default function ContentOptimizer() {
    const [url, setUrl] = useState("");
    const [content, setContent] = useState("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<any>(null);

    const fetchUrlMutation = trpc.contentOptimizer.fetchUrl.useMutation();
    const analyzeMutation = trpc.contentOptimizer.analyze.useMutation();
    const addTrackingKeywordMutation = trpc.contentOptimizer.addTrackingKeyword.useMutation();

    // Auto-fetch if URL param exists
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlParam = params.get("url");
        if (urlParam) {
            setUrl(urlParam);
            handleFetchUrl(urlParam);
        }
    }, []);

    const handleFetchUrl = async (targetUrl: string) => {
        if (!targetUrl) return;

        try {
            toast.info("正在抓取網頁內容...");
            const result = await fetchUrlMutation.mutateAsync({ url: targetUrl });

            if (result.content) {
                setContent(result.content);
                toast.success("抓取成功！");
                // Auto analyze after fetch
                handleAnalyze(result.content, targetUrl);
            } else {
                toast.error("抓取失敗，系統已記錄");
            }
        } catch (error: any) {
            toast.error("抓取失敗，系統已記錄");
        }
    };

    const handleAnalyze = async (textToAnalyze: string, sourceUrl?: string) => {
        if (!textToAnalyze.trim()) {
            toast.error("請先輸入內容或網址");
            return;
        }

        setIsAnalyzing(true);
        setAnalysisResult(null);

        try {
            const result = await analyzeMutation.mutateAsync({
                content: textToAnalyze,
                sourceUrl: sourceUrl || url || undefined,
            });
            setAnalysisResult(result);
            toast.success("分析完成！");
        } catch (error: any) {
            toast.error("分析失敗，系統已記錄");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleAddTrackingKeyword = async (
        keyword: string,
        category?: string | null,
        primaryIntent?: "informational" | "commercial" | "transactional"
    ) => {
        try {
            const result = await addTrackingKeywordMutation.mutateAsync({
                keyword,
                category: category || undefined,
                primaryIntent,
            });
            if (!result.success) {
                toast.info(result.message || "已在追蹤名單中");
                return;
            }
            toast.success(`已加入追蹤：${keyword}`);
            await handleAnalyze(content, url || undefined);
        } catch (error: any) {
            toast.error("加入追蹤失敗，系統已記錄");
        }
    };

    return (
        <DashboardLayout>
            <div className="container py-8 space-y-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
                            <Wand2 className="h-8 w-8 text-primary" />
                            文章檢查 <span className="text-sm font-normal text-muted-foreground bg-slate-100 px-2 py-1 rounded-full">v6.0</span>
                        </h1>
                        <p className="text-muted-foreground">
                            針對潛在意圖、頁面問題與引用結果進行整理，協助內容團隊安排優先修正項目。
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left Column: Input Area */}
                    <div className="lg:col-span-5 space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>內容來源</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <LinkIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="輸入文章網址 (URL)..."
                                            className="pl-9"
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                        />
                                    </div>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleFetchUrl(url)}
                                        disabled={fetchUrlMutation.isPending}
                                    >
                                        {fetchUrlMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "抓取"}
                                    </Button>
                                </div>

                                <div className="relative">
                                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-white to-transparent h-4 pointer-events-none" />
                                    <Textarea
                                        placeholder="或直接在此貼上 HTML 原始碼..."
                                        className="h-[400px] font-mono text-xs resize-none overflow-auto"
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                    />
                                    <div className="absolute bottom-2 right-2 text-xs text-muted-foreground bg-white/80 px-2 rounded">
                                        {content.length} chars
                                    </div>
                                </div>

                                <Button
                                    className="w-full"
                                    size="lg"
                                    onClick={() => handleAnalyze(content)}
                                    disabled={isAnalyzing || !content}
                                >
                                    {isAnalyzing ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            正在分析收錄風險、意圖與 AI 實績...
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 className="mr-2 h-4 w-4" />
                                            開始分析
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column: Results Area */}
                    <div className="lg:col-span-7 space-y-6">
                        {analysisResult ? (
                            <Tabs defaultValue="dashboard">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="dashboard">潛在意圖</TabsTrigger>
                                    <TabsTrigger value="recommendations">問題與建議</TabsTrigger>
                                </TabsList>
                                <TabsContent value="dashboard" className="mt-4">
                                    <ResultsDashboard
                                        results={analysisResult}
                                        isAddingTracking={addTrackingKeywordMutation.isPending}
                                        onAddTrackingKeyword={handleAddTrackingKeyword}
                                    />
                                </TabsContent>
                                <TabsContent value="recommendations" className="mt-4">
                                    <Recommendations
                                        results={analysisResult}
                                        isAddingTracking={addTrackingKeywordMutation.isPending}
                                        onAddTrackingKeyword={handleAddTrackingKeyword}
                                    />
                                </TabsContent>
                            </Tabs>
                        ) : (
                            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg bg-slate-50/50">
                                <FileText className="h-16 w-16 mb-4 opacity-20" />
                                <p>請在左側輸入網址或內容以開始分析</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
